import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { DocumentListRow } from '@/components/rfid/DocumentListRow'
import { DocumentListToolbar } from '@/components/rfid/DocumentListToolbar'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { getMovement, type MovementId } from '@/config/movements'
import { rfidService, type RfidDocumentRow } from '@/services/rfid.service'
import { useAuthStore } from '@/stores/authStore'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import { getEmployeeCode } from '@/utils/employeeCode'
import { customerCodeFromDocument } from '@/utils/customerDetails'
import { movementFlowLabel } from '@/utils/movementFlow'
import {
  applyDocumentListFilters,
  buildCustomerFilterOptions,
  buildPlantFilterOptions,
  EMPTY_DOCUMENT_LIST_FILTERS,
  type DocumentListFilterState,
} from '@/utils/documentListFilters'

const MOVEMENT_IDS = new Set<string>([
  'allot',
  'custin',
  'dehire',
  'dehire_received',
  'relocation_out',
  'relocation_in',
  'transfer_out',
  'transfer_in',
])

function isMovementId(id: string | undefined): id is MovementId {
  return Boolean(id && MOVEMENT_IDS.has(id))
}

function partyName(row: RfidDocumentRow): string {
  return String(
    row.CustName ??
      row.Customer ??
      row.ReceiverName ??
      row.SenderName ??
      row.Receiver ??
      row.Sender ??
      '—'
  )
}

export default function DocumentListPage() {
  const { movementId: rawId } = useParams<{ movementId: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const employeeCode = getEmployeeCode(user)
  const initFromDocument = useDocumentWizardStore((s) => s.initFromDocument)
  const loadCustomerPostingContext = useDocumentWizardStore((s) => s.loadCustomerPostingContext)
  const [filters, setFilters] = useState<DocumentListFilterState>(EMPTY_DOCUMENT_LIST_FILTERS)
  const [opening, setOpening] = useState(false)

  const validMovement = isMovementId(rawId)
  const movement = validMovement ? getMovement(rawId) : null
  const listBacked = Boolean(movement?.listBacked)

  const docsQ = useQuery({
    queryKey: ['rfid-docs', movement?.id, employeeCode],
    queryFn: () => rfidService.fetchDocumentList(movement!, employeeCode),
    enabled: Boolean(employeeCode && listBacked && movement),
  })

  const rows = useMemo(
    () => (Array.isArray(docsQ.data) ? docsQ.data : []),
    [docsQ.data]
  )

  const plantOptions = useMemo(() => buildPlantFilterOptions(rows), [rows])
  const customerOptions = useMemo(() => buildCustomerFilterOptions(rows), [rows])

  const filtered = useMemo(
    () => applyDocumentListFilters(rows, filters),
    [rows, filters]
  )

  const patchFilters = useCallback((patch: Partial<DocumentListFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_DOCUMENT_LIST_FILTERS)
  }, [])

  if (!validMovement) {
    return <Navigate to="/" replace />
  }

  if (!listBacked && movement) {
    if (movement.id === 'transfer_out' || movement.id === 'transfer_in') {
      return <Navigate to={`/transfer/customer?movement=${movement.id}`} replace />
    }
    return <Navigate to="/" replace />
  }

  const openDocument = async (doc: RfidDocumentRow) => {
    if (!movement || opening) return
    setOpening(true)
    try {
      initFromDocument(movement.id, doc)
      await loadCustomerPostingContext(customerCodeFromDocument(doc, movement.id))
      const detailsError = useDocumentWizardStore.getState().customerDetailsError
      if (detailsError) {
        toast.warning(detailsError)
      }
      navigate('/wizard/capture')
    } finally {
      setOpening(false)
    }
  }

  return (
    <div className="rfid-web-frame">
      <RfidAppHeader
        title={movement!.title}
        subtitle={movementFlowLabel(movement!)}
        onBack={() => navigate('/')}
      />

      <div className="rfid-web-body overflow-y-auto">
        {docsQ.isLoading ? (
          <PageLoading tip="Loading documents…" />
        ) : docsQ.isError ? (
          <PageError
            message="Failed to load documents"
            description={(docsQ.error as Error)?.message}
          />
        ) : (
          <>
            <DocumentListToolbar
              filters={filters}
              onChange={patchFilters}
              onClear={clearFilters}
              customerOptions={customerOptions}
              plantOptions={plantOptions}
              resultCount={filtered.length}
            />

            {rows.length === 0 ? (
              <PageEmpty description="No documents found for this movement." />
            ) : filtered.length === 0 ? (
              <PageEmpty description="No documents match your filters." />
            ) : (
              <div
                className={`rfid-doc-list ${opening ? 'pointer-events-none opacity-60' : ''}`}
              >
                {filtered.map((row, i) => (
                  <DocumentListRow
                    key={String(row.DocumentNo ?? i)}
                    document={{ ...row, CustName: partyName(row) }}
                    onClick={() => void openDocument(row)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
